import {
  BRAND_DISPLAY_NAME,
  BRAND_WORDMARK_ACCENT_SUFFIX,
} from '@streamlain/brand';
import type { AppBaseConfig } from '@streamlain/view-config';
import type { MouseEvent } from 'react';
import { createElement } from 'react';
import { z } from 'zod';

import { VIEW_COMPONENT_TYPES } from '../../../constants/view-component-types';
import type {
  ViewComponent,
  ViewComponentRenderProps,
} from '../../../types/view-component.types';
import { resolveAppBaseRuntimeHref } from '../runtime-href';
import * as styles from '../styles.css';
import { LanguageSwitcherComponent } from './language-switcher';

const MAX_RENDER_DEPTH = 3;

const configSchema = z.object({
  visible: z.boolean().default(true),
  showDraftRoutes: z.boolean().default(true),
  orientation: z.enum(['auto', 'header', 'sidebar']).default('auto'),
});

interface NavigationEntry {
  id: string;
  label: string;
  href: string;
  depth: number;
}

interface AppBaseNavigationBranding {
  displayName: string;
  logoUrl: string | null;
}

function AppBaseNavigationRenderer({
  config,
  mode,
  runtimeContext,
}: ViewComponentRenderProps) {
  const { showDraftRoutes, visible } = config as z.infer<typeof configSchema>;
  // Persisted components created before the visibility option have no field.
  // Only an explicit opt-out hides the topbar.
  if (visible === false) return null;
  const appBaseConfig = readAppBaseConfig(runtimeContext);
  const branding = readAppBaseBranding(runtimeContext);
  const entries = appBaseConfig
    ? listNavigationEntries(
        appBaseConfig,
        showDraftRoutes,
        runtimeContext?.locale
      )
    : [];
  const pageShape =
    runtimeContext?.view?.['pageShape'] === 'sidebar' ? 'sidebar' : 'header';
  const isSidebar =
    config.orientation === 'sidebar' ||
    (config.orientation !== 'header' && pageShape === 'sidebar');
  const isBuilder = mode === 'builder';
  const availableLocales = appBaseConfig?.availableLocales ?? [];
  const showLanguageSwitcher =
    availableLocales.filter(
      (locale, index, locales) => locales.indexOf(locale) === index
    ).length > 1;

  return createElement(
    'header',
    {
      className: styles.appBaseNavigation,
      'data-sl-role': 'app-base-navigation',
      'data-sl-orientation': isSidebar ? 'sidebar' : 'header',
    },
    createBrandLink({ branding, isBuilder, isSidebar, runtimeContext }),
    createElement(
      'div',
      {
        className: styles.appBaseNavigationActions,
      },
      createElement(
        'nav',
        {
          className: styles.appBaseNavigationLinks,
          'data-sl-role': 'navigation-links',
          'aria-label': 'App navigation',
        },
        ...entries.map(entry =>
          createElement(
            'a',
            {
              key: entry.id,
              // Preview keeps the real href and lets its route owner intercept
              // the bubbling click. Builder canvas remains non-navigable.
              href: resolveAppBaseRuntimeHref(entry.href, runtimeContext),
              onClick: (event: MouseEvent<HTMLAnchorElement>) => {
                if (isBuilder) event.preventDefault();
              },
              className: styles.appBaseNavigationLink,
              'data-sl-role': 'nav-link',
              style: {
                marginLeft: isSidebar ? Math.min(entry.depth, 2) * 12 : 0,
                fontWeight: entry.depth === 0 ? 600 : 500,
              },
            },
            !isSidebar && entry.depth > 0 ? `> ${entry.label}` : entry.label
          )
        )
      ),
      showLanguageSwitcher
        ? LanguageSwitcherComponent.render({
            config: LanguageSwitcherComponent.defaultConfig,
            data: [],
            mode,
            runtimeContext,
          })
        : null
    )
  );
}

function createBrandLink(input: {
  branding: AppBaseNavigationBranding;
  isBuilder: boolean;
  isSidebar: boolean;
  runtimeContext?: ViewComponentRenderProps['runtimeContext'];
}) {
  const { branding, isBuilder, isSidebar, runtimeContext } = input;
  return createElement(
    'a',
    {
      key: 'app-base-brand-home',
      href: resolveAppBaseRuntimeHref('/', runtimeContext),
      onClick: (event: MouseEvent<HTMLAnchorElement>) => {
        if (isBuilder) event.preventDefault();
      },
      className: styles.appBaseNavigationBrand,
      'data-sl-role': 'brand',
      style: { marginBottom: isSidebar ? 4 : 0 },
    },
    createBrandLogo(branding),
    createElement(
      'span',
      { className: styles.appBaseNavigationBrandText },
      createBrandText(branding.displayName)
    )
  );
}

function createBrandText(displayName: string) {
  const wordmark = BRAND_DISPLAY_NAME;
  if (displayName !== wordmark && !displayName.startsWith(`${wordmark} `)) {
    return displayName;
  }
  const suffix = displayName.slice(wordmark.length);
  if (!BRAND_WORDMARK_ACCENT_SUFFIX) {
    return `${wordmark}${suffix}`;
  }
  return createElement(
    'span',
    null,
    wordmark.slice(0, -BRAND_WORDMARK_ACCENT_SUFFIX.length),
    createElement(
      'span',
      {
        className: styles.appBaseNavigationBrandAccent,
        'data-sl-role': 'brand-accent',
      },
      BRAND_WORDMARK_ACCENT_SUFFIX
    ),
    suffix
  );
}

function createBrandLogo(branding: AppBaseNavigationBranding) {
  return branding.logoUrl
    ? createElement('img', {
        src: branding.logoUrl,
        alt: branding.displayName,
        className: styles.appBaseNavigationLogo,
      })
    : createElement(
        'span',
        {
          className: styles.appBaseNavigationLogoFallback,
          'aria-label': branding.displayName,
          role: 'img',
        },
        brandInitial(branding.displayName)
      );
}

function brandInitial(displayName: string) {
  return (
    displayName.trim().charAt(0).toLocaleUpperCase() ||
    BRAND_DISPLAY_NAME.charAt(0).toLocaleUpperCase()
  );
}

function readAppBaseConfig(
  runtimeContext: ViewComponentRenderProps['runtimeContext']
): AppBaseConfig | null {
  const appBase = runtimeContext?.appBase;
  const raw =
    appBase && typeof appBase === 'object' && 'config' in appBase
      ? (appBase as { config?: unknown }).config
      : null;
  return raw &&
    typeof raw === 'object' &&
    Array.isArray((raw as AppBaseConfig).pages) &&
    Array.isArray((raw as AppBaseConfig).navigation)
    ? (raw as AppBaseConfig)
    : null;
}

function readAppBaseBranding(
  runtimeContext: ViewComponentRenderProps['runtimeContext']
): AppBaseNavigationBranding {
  const appBase = runtimeContext?.appBase;
  const raw =
    appBase && typeof appBase === 'object' && 'branding' in appBase
      ? (appBase as { branding?: unknown }).branding
      : null;
  const record =
    raw && typeof raw === 'object' && !Array.isArray(raw)
      ? (raw as Record<string, unknown>)
      : null;
  const settings =
    record?.['settings'] &&
    typeof record['settings'] === 'object' &&
    !Array.isArray(record['settings'])
      ? (record['settings'] as Record<string, unknown>)
      : null;
  const displayName =
    typeof settings?.['displayName'] === 'string' &&
    settings['displayName'].trim()
      ? settings['displayName'].trim()
      : 'Home';
  const logoUrl =
    typeof record?.['logoUrl'] === 'string' && record['logoUrl'].trim()
      ? record['logoUrl'].trim()
      : null;
  return { displayName, logoUrl };
}

function listNavigationEntries(
  appBaseConfig: AppBaseConfig,
  showDraftRoutes: boolean,
  locale?: string
): NavigationEntry[] {
  const pagesById = new Map(appBaseConfig.pages.map(page => [page.id, page]));
  const sorted = [...appBaseConfig.navigation].sort(
    (a, b) => a.order - b.order
  );
  const byId = new Map(sorted.map(item => [item.id, item]));
  const byParent = new Map<string | null, typeof sorted>();
  for (const item of sorted) {
    const parentId =
      item.parentId && byId.has(item.parentId) ? item.parentId : null;
    const list = byParent.get(parentId) ?? [];
    list.push(item);
    byParent.set(parentId, list);
  }

  const result: NavigationEntry[] = [];
  const visited = new Set<string>();
  const pushBranch = (parentId: string | null, depth: number) => {
    for (const item of byParent.get(parentId) ?? []) {
      if (visited.has(item.id) || depth >= MAX_RENDER_DEPTH) continue;
      visited.add(item.id);
      const page = item.pageId ? pagesById.get(item.pageId) : null;
      if (page && (showDraftRoutes || page.status === 'published')) {
        result.push({
          id: item.id,
          label: resolveNavigationLabel(item, locale, page.id),
          href: page.path,
          depth,
        });
      } else if (!page && item.href) {
        result.push({
          id: item.id,
          label: resolveNavigationLabel(item, locale, item.href),
          href: item.href,
          depth,
        });
      }
      pushBranch(item.id, depth + 1);
    }
  };
  pushBranch(null, 0);
  return result;
}

function resolveNavigationLabel(
  item: AppBaseConfig['navigation'][number],
  locale: string | undefined,
  fallback: string
): string {
  const normalizedLocale = locale?.trim().toLowerCase();
  const baseLocale = normalizedLocale?.split(/[-_]/)[0];
  const localized =
    (normalizedLocale ? item.labelsI18n?.[normalizedLocale] : undefined) ??
    (baseLocale ? item.labelsI18n?.[baseLocale] : undefined);
  if (localized) return localized;
  return item.labelFormula?.type === 'value'
    ? String(item.labelFormula.value)
    : fallback;
}

export const AppBaseNavigationComponent: ViewComponent = {
  id: VIEW_COMPONENT_TYPES.APP_BASE_NAVIGATION,
  type: 'navigation',
  name: 'AppBase Navigation',
  icon: createElement(
    'span',
    { style: { fontSize: 'var(--sl-app-font-size-sm)' } },
    'Nav'
  ),
  capabilities: [],
  configSchema,
  defaultConfig: {
    visible: true,
    showDraftRoutes: true,
    orientation: 'auto',
  },
  render(props) {
    return createElement(AppBaseNavigationRenderer, props);
  },
  palettePreview() {
    return createElement(
      'div',
      { className: styles.palettePreview },
      'Nav AppBase Navigation'
    );
  },
};
